import { useEffect, useRef, useState } from 'react';
import { MAP_AMBIENT_VOLUME, getMapAmbientAudio } from '../../infrastructure/mapAmbientMusic';

const MUTED_STORAGE_KEY = 'nacanaca-map-music-muted';
const TIME_STORAGE_KEY = 'nacanaca-map-music-time';

const readStoredMuted = () => {
  try {
    return localStorage.getItem(MUTED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const readStoredTime = () => {
  try {
    const value = Number(localStorage.getItem(TIME_STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
};

// Solo suena en el mapa general (pantalla hub). Al desmontarse
// (salir del mapa / entrar al juego) se pausa y se guarda la posición,
// así al volver o al refrescar la página sigue donde iba.
export default function MapAmbientAudio() {
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(readStoredMuted);
  const [needsGesture, setNeedsGesture] = useState(false);

  useEffect(() => {
    // Reproductor compartido: el archivo se empezó a descargar desde el
    // arranque de la app, así que al llegar al mapa ya está (casi) listo.
    const audio = getMapAmbientAudio();
    audioRef.current = audio;

    audio.loop = true;
    audio.volume = MAP_AMBIENT_VOLUME;
    audio.muted = readStoredMuted();

    let cancelled = false;
    let lastSavedAt = 0;

    const savePosition = () => {
      try {
        if (Number.isFinite(audio.currentTime) && audio.currentTime > 0) {
          localStorage.setItem(TIME_STORAGE_KEY, String(Math.floor(audio.currentTime)));
        }
      } catch {
        // Sin almacenamiento: la música sigue sonando igual.
      }
    };

    const applyStartPosition = () => {
      // Si el login ya la puso a sonar (startMapAmbientMusic), no se
      // rebusca: interrumpirla al montar el mapa se escucha como un corte.
      if (!audio.paused) return;
      const savedTime = readStoredTime();
      try {
        if (
          savedTime > 0
          && Number.isFinite(audio.duration)
          && savedTime < audio.duration - 1
        ) {
          audio.currentTime = savedTime;
        }
      } catch {
        // Si el navegador no deja saltar, arranca desde el inicio.
      }
    };

    // El login ya intentó reproducir al instante; si sigue sonando, el mapa
    // solo la acompaña (no la reinicia ni la rebusca).
    const alreadyPlaying = !audio.paused && !audio.ended;
    if (!alreadyPlaying) {
      if (audio.readyState >= 1) {
        applyStartPosition();
      } else {
        audio.addEventListener('loadedmetadata', applyStartPosition, { once: true });
      }
    }

    const startPlayback = () => {
      if (cancelled || audio.muted) return;
      audio.play().then(() => {
        if (!cancelled) setNeedsGesture(false);
      }).catch(() => {
        if (cancelled) return;
        setNeedsGesture(true);
        window.addEventListener('pointerdown', unlockOnGesture, { once: true });
        window.addEventListener('keydown', unlockOnGesture, { once: true });
      });
    };

    const unlockOnGesture = () => {
      if (cancelled || audio.muted) return;
      audio.play().then(() => {
        if (!cancelled) setNeedsGesture(false);
      }).catch(() => {});
    };

    // Los navegadores móviles bloquean el autoplay sin gesto previo.
    // Como al mapa se llega tras varios clics —y el login ya disparó
    // startMapAmbientMusic()—, normalmente entra directo; si no, se
    // reintenta con el primer toque/tecla o con el botón bocina.
    if (!alreadyPlaying) {
      startPlayback();
    }

    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastSavedAt > 2000) {
        lastSavedAt = now;
        savePosition();
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        savePosition();
        audio.pause();
      } else {
        startPlayback();
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', savePosition);

    return () => {
      cancelled = true;
      savePosition();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', applyStartPosition);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', savePosition);
      window.removeEventListener('pointerdown', unlockOnGesture);
      window.removeEventListener('keydown', unlockOnGesture);
      audio.pause();
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    try {
      localStorage.setItem(MUTED_STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Sin almacenamiento: solo afecta a esta sesión.
    }
    const audio = audioRef.current;
    if (audio) {
      audio.muted = next;
      if (!next) {
        audio.play().then(() => setNeedsGesture(false)).catch(() => setNeedsGesture(true));
      }
    }
  };

  return (
      <button
        type="button"
        onClick={toggleMuted}
        aria-pressed={muted}
        aria-label={muted ? 'Activar música del mapa' : 'Silenciar música del mapa'}
        title={muted ? 'Activar música' : 'Silenciar música'}
        className={`flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center bg-transparent text-[#F7E04B] transition-opacity hover:opacity-80 active:opacity-60 ${needsGesture && !muted ? 'animate-pulse' : ''}`}
      >
        {muted ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
            <line x1="15" y1="9" x2="21" y2="15" />
            <line x1="21" y1="9" x2="15" y2="15" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
            <path d="M15.5 8.5a5 5 0 0 1 0 7" />
            <path d="M18.5 5.5a9 9 0 0 1 0 13" />
          </svg>
        )}
      </button>
  );
}
